import { Icon } from '@iconify/react';
import { useEffect, useRef, useState } from 'react';

import { Botao, Campo, Card, Carregando, ErroInline, Tag, TituloTela } from '../components/Sga';
import { api, subirLogo } from '../lib/api';
import type { Marca } from '../lib/types';

/** Sugestões prontas. Todas passam AA no cabeçalho — é o ponto de partida
 *  seguro para quem não tem um hex da marca à mão. */
const SUGESTOES = [
  { cor: '#01a863', nome: 'Verde SGA' },
  { cor: '#1668dc', nome: 'Azul' },
  { cor: '#e8590c', nome: 'Laranja' },
  { cor: '#c2255c', nome: 'Magenta' },
  { cor: '#6741d9', nome: 'Roxo' },
  { cor: '#0c8599', nome: 'Petróleo' },
  { cor: '#2b2b2b', nome: 'Grafite' },
];

/**
 * Marca do tenant: o que o cliente final vê na página de suporte.
 *
 * Cada representante edita a sua. Um cliente da matriz abre a página verde da
 * SGA; um cliente de representante abre a página com o logo e a cor dele — e
 * isso sai de graça do roteamento, porque `clientes.tenant_id` já é quem
 * atende.
 *
 * A cor é uma só. Todo o resto é derivado no gateway com o contraste medido:
 * abrir a paleta inteira destruiria a conformidade AA na primeira escolha de
 * um amarelo claro, e essa é a página que o cliente abre no celular ao sol.
 */
export function MarcaTela({ periodo, podeEscrever }: { periodo: string; podeEscrever: boolean }) {
  const [marca, setMarca] = useState<Marca | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  // Rascunho local: a prévia responde a cada tecla, mas só o "Salvar" grava.
  const [cor, setCor] = useState('#01a863');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [dias, setDias] = useState<number[]>([1, 2, 3, 4, 5]);

  const aplicar = (m: Marca) => {
    setMarca(m);
    setCor(m.cor_primaria);
    setNome(m.nome_exibicao ?? '');
    setTelefone(m.suporte_telefone ?? '');
    setEmail(m.suporte_email ?? '');
    setInicio(m.atendimento_inicio ?? '');
    setFim(m.atendimento_fim ?? '');
    setDias(m.atendimento_dias ?? []);
  };

  useEffect(() => {
    api
      .get<{ marca: Marca }>('/marca')
      .then((d) => aplicar(d.marca))
      .catch((e) => setErro((e as Error).message));
  }, []);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const d = await api.put<{ marca: Marca }>('/marca', {
        cor_primaria: cor,
        nome_exibicao: nome || null,
        // Telefone e e-mail sairam da tela mas continuam sendo **reenviados
        // como vieram**, pelo mesmo motivo do horario: o gateway grava o que
        // recebe, e omitir apagaria em silencio o que ja estava cadastrado.
        suporte_telefone: telefone || null,
        suporte_email: email || null,
        // O horario nao e mais editavel aqui — mas continua sendo **reenviado
        // como veio**. O gateway grava o que recebe: omitir estes campos
        // apagaria em silencio o horario do tenant, que ainda e o padrao de
        // quem nao configurou faixa no link.
        atendimento_inicio: inicio || null,
        atendimento_fim: fim || null,
        atendimento_dias: dias,
        // Campo ainda exigido pela API. A vitrine saiu da pagina de suporte;
        // enquanto a coluna existir no banco, mando o valor que a desliga.
        vitrine_visivel: false,
      });
      aplicar(d.marca);
      setSalvo(true);
      window.setTimeout(() => setSalvo(false), 2200);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  const enviarLogo = async (arquivo: File) => {
    setErro(null);
    try {
      const d = await subirLogo(arquivo);
      aplicar(d.marca);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  const removerLogo = async () => {
    if (!window.confirm('Remover o logo? A página de suporte passa a mostrar só o nome.')) return;
    try {
      const d = await api.del<{ marca: Marca }>('/marca/logo');
      aplicar(d.marca);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  if (!marca) {
    return (
      <div className="tela-fixa">
        <TituloTela icone="mdi:palette-outline" titulo="Marca e atendimento" contexto={periodo} />
        {erro ? <ErroInline mensagem={erro} /> : <Carregando />}
      </div>
    );
  }

  // A paleta exibida é a **salva**, não o rascunho: o gateway é quem deriva, e
  // duplicar o cálculo aqui abriria espaço para os dois discordarem.
  const p = marca.paleta;
  const corMudou = cor.toLowerCase() !== marca.cor_primaria.toLowerCase();

  return (
    <div className="tela-fixa">
      <TituloTela
        icone="mdi:palette-outline"
        titulo="Marca e atendimento"
        contexto={periodo}
        acoes={
          podeEscrever && (
            <>
              {salvo && <Tag>Salvo</Tag>}
              <Botao variante="primary" icone="mdi:content-save-outline" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </Botao>
            </>
          )
        }
      />

      {erro && <ErroInline mensagem={erro} />}

      <div className="sga-grid sga-grid--marca">
        {/* ------------------------------------------------------ edição */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Card titulo="Identidade">
            <div className="cad-dados" style={{ marginBottom: 14 }}>
              <Campo rotulo="Nome na página de suporte" hint="Vazio usa o nome do tenant.">
                <input
                  className="input"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder={marca.nome_exibicao ?? 'SGA'}
                  disabled={!podeEscrever}
                />
              </Campo>
            </div>

            <div className="marca-logo">
              <div className="marca-logo__previa" style={{ background: p.primaria }}>
                {marca.logo_url ? (
                  <img src={marca.logo_url} alt="Logo" />
                ) : (
                  <span style={{ color: p.sobre_primaria, fontWeight: 600 }}>
                    {nome || 'Sem logo'}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>Logo</div>
                <div className="hint" style={{ marginTop: 0, marginBottom: 8 }}>
                  PNG, JPG ou WEBP até 512 KB. SVG não é aceito porque pode conter script.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Botao
                    icone="mdi:upload"
                    onClick={() => arquivoRef.current?.click()}
                    disabled={!podeEscrever}
                  >
                    {marca.logo_url ? 'Trocar' : 'Enviar'}
                  </Botao>
                  {marca.logo_url && (
                    <Botao variante="danger" icone="mdi:delete-outline" onClick={removerLogo} disabled={!podeEscrever} />
                  )}
                </div>
                <input
                  ref={arquivoRef}
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const a = e.target.files?.[0];
                    e.target.value = '';
                    if (a) enviarLogo(a);
                  }}
                />
              </div>
            </div>
          </Card>

        </div>

        {/* ------------------------------------------------------- cor
            Ocupa a coluna que era do contato de suporte. A escolha da cor
            merece largura: o seletor, a amostra e a explicacao do contraste
            derivado nao cabiam bem espremidos embaixo da identidade. */}
        <Card titulo="Cor da marca">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
            <input
            type="color"
            className="marca-cor"
            value={cor}
            onChange={(e) => setCor(e.target.value)}
            disabled={!podeEscrever}
            aria-label="Cor da marca"
            />
            <Campo rotulo="Hexadecimal" flex={1}>
            <input
              className="input mono"
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              maxLength={7}
              disabled={!podeEscrever}
            />
            </Campo>
          </div>

          <div className="hint" style={{ marginTop: 0, marginBottom: 6 }}>
            Sugestões — todas legíveis no cabeçalho
          </div>
          <div className="marca-sugestoes">
            {SUGESTOES.map((s) => (
            <button
              key={s.cor}
              type="button"
              className={`marca-sugestao${cor.toLowerCase() === s.cor ? ' marca-sugestao--ativa' : ''}`}
              style={{ background: s.cor }}
              title={`${s.nome} · ${s.cor}`}
              onClick={() => setCor(s.cor)}
              disabled={!podeEscrever}
              aria-label={s.nome}
            />
            ))}
          </div>

          {corMudou && (
            <div className="sga-aviso-warn" style={{ marginTop: 12 }}>
            <Icon icon="mdi:information-outline" width={17} />
            A prévia abaixo mostra a cor <strong>salva</strong>. Clique em Salvar para ver a nova.
            </div>
          )}

          {/* O contraste é medido pelo gateway e mostrado aqui: o operador vê
            o número antes de mandar a cor para a página do cliente. */}
          <table className="sga-table sga-table--densa" style={{ marginTop: 12 }}>
            <thead>
            <tr>
              <th>Uso</th>
              <th style={{ width: 110 }}>Cor</th>
              <th style={{ width: 90, textAlign: 'right' }}>Contraste</th>
              <th style={{ width: 70 }}>WCAG</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>Texto sobre a cor (cabeçalho)</td>
              <td className="mono">{p.sobre_primaria}</td>
              <td className="num">{p.contraste_sobre_primaria.toFixed(2)}:1</td>
              <td>
              {p.aa_no_cabecalho ? <Tag>AA</Tag> : <Tag kind="err">baixo</Tag>}
              </td>
            </tr>
            <tr>
              <td>A cor como texto sobre branco</td>
              <td className="mono">{p.primaria_texto}</td>
              <td className="num">{p.contraste_texto.toFixed(2)}:1</td>
              <td>
              <Tag>AA</Tag>
              </td>
            </tr>
            </tbody>
          </table>

          <div className="hint" style={{ marginTop: 8 }}>
            As cores de texto são derivadas da sua: o sistema escurece e escolhe entre branco e
            preto até atingir o mínimo legível. Você escolhe uma cor, não uma paleta.
          </div>
        </Card>

      </div>
    </div>
  );
}
